
import {
  defineComponent, PropType, toRef, Ref, SetupContext, ref, onMounted, onUnmounted, watch, nextTick,
} from 'vue';
import ScrollPanel from 'primevue/scrollpanel';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { TemplatesElementsHistoryGroup } from '@/types/generated-types/graphql';
import { hideLeftToolbar } from '@/composables/template-editor/TemplateEditor';
import Panel from 'primevue/panel';

export default defineComponent({
  name: 'HistoryPanel',

  components: {
    ScrollPanel,
    Button,
    Panel,
  },

  props: {
    historyList: {
      type: Array as PropType<TemplatesElementsHistoryGroup[]>,
      required: true,
    },

    selectedHistory: {
      type: Object as PropType<TemplatesElementsHistoryGroup|null>,
      required: false,
      default: () => null,
    },

    hasMore: {
      type: Boolean,
      required: false,
      default: false,
    },

    isLoadingMore: {
      type: Boolean,
      required: false,
      default: false,
    },

    totalItems: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  emits: ['on-change-history', 'load-more'],

  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();
    const selected: Ref<TemplatesElementsHistoryGroup|null> = toRef(props, 'selectedHistory');
    const loadMoreTrigger = ref<HTMLElement | null>(null);
    const scrollPanelRef = ref<any>(null);
    let observer: IntersectionObserver | null = null;

    const handleChangeHistory = async (historyIndex: number) => {
      emit('on-change-history', historyIndex);
    };

    const setupIntersectionObserver = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      if (!props.hasMore) {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && props.hasMore && !props.isLoadingMore) {
            emit('load-more');
          }
        },
        {
          root: scrollPanelRef.value?.$el?.querySelector('.p-scrollpanel-content') || null,
          rootMargin: '100px',
          threshold: 0.1,
        },
      );

      if (loadMoreTrigger.value) {
        observer.observe(loadMoreTrigger.value);
      }
    };

    watch(() => props.hasMore, (newVal) => {
      if (newVal) {
        setTimeout(() => {
          setupIntersectionObserver();
        }, 100);
      }
    });

    watch(() => props.isLoadingMore, (newVal, oldVal) => {
      if (oldVal && !newVal && props.hasMore) {
        nextTick(() => {
          setTimeout(() => {
            setupIntersectionObserver();
          }, 100);
        });
      }
    });

    onMounted(() => {
      setTimeout(() => {
        setupIntersectionObserver();
      }, 100);
    });

    onUnmounted(() => {
      if (observer) {
        observer.disconnect();
      }
    });

    return {
      t,
      selected,
      handleChangeHistory,
      hideLeftToolbar,
      loadMoreTrigger,
      scrollPanelRef,
    };
  },
});
